// settings_kv table — one row per logical setting key, value is JSON.
// This module defines the value shapes for known keys, NOT the raw row itself
// (which is just { key, value: unknown, updatedAt }). The row-level GET
// endpoint should validate by key — `/api/config/notifications_config` parses
// through `notificationsConfigSchema`, not through a generic row schema.
//
// Add new key shapes here as they emerge. Keep value schemas loose
// (`.passthrough()` or `.catchall(z.unknown())`) when the server treats the
// blob as opaque.

import { z } from 'zod';
import { timestampSchema } from './shared';

// ─── Generic row shape (any key) ─────────────────────────────
export const settingsKvRowSchema = z.object({
  key: z.string().min(1),
  value: z.unknown(),
  updatedAt: timestampSchema,
});

// ─── Known value shapes by key ───────────────────────────────

export const telegramChannelConfigSchema = z.object({
  enabled: z.boolean().default(false),
  botToken: z.string().optional(),
  chatIds: z.array(z.string()).default([]),
});

export const resendChannelConfigSchema = z.object({
  enabled: z.boolean().default(false),
  apiKey: z.string().optional(),
  from: z.string().optional(),
  recipients: z.array(z.string()).default([]),
});

export const smtpChannelConfigSchema = z.object({
  enabled: z.boolean().default(false),
  host: z.string().optional(),
  port: z.number().int().optional(),
  user: z.string().optional(),
  pass: z.string().optional(),
  secure: z.boolean().default(false),
  from: z.string().optional(),
  recipients: z.array(z.string()).default([]),
});

/** Value shape for `settings_kv.notifications_config`. */
export const notificationsConfigSchema = z.object({
  telegram: telegramChannelConfigSchema.optional(),
  email_resend: resendChannelConfigSchema.optional(),
  email_smtp: smtpChannelConfigSchema.optional(),
});

/**
 * Value shape for `settings_kv.newsletter_config` — ListMonk integration.
 * `apiToken` is the only secret (encrypted at rest via SECRET_PATHS_BY_KEY in
 * server/routes/config.ts; the whole key is in ADMIN_ONLY_KEYS so it never
 * reaches anonymous callers).
 */
export const newsletterConfigSchema = z.object({
  enabled: z.boolean().default(false),
  baseUrl: z.string().default('https://newsletter.chinedukoggu.com'),
  apiUsername: z.string().default('personal-website'),
  apiToken: z.string().optional(), // SECRET — encrypted at rest
  listUuid: z.string().default('a7b2626d-6b47-4162-84b8-c8733eabd4c2'),
  fromEmail: z.string().optional(),
  fromName: z.string().optional(),
  subjectPrefix: z.string().default(''),
  autoSendOnPublish: z.boolean().default(false),
  campaignBodyMode: z.enum(['teaser', 'full']).default('teaser'),
});

/** Value shape for `settings_kv.bootstrap_status` (and the admin endpoint). */
export const bootstrapStatusSchema = z.object({
  hasAdmin: z.boolean(),
});

export type SettingsKvRow = z.infer<typeof settingsKvRowSchema>;
export type NotificationsConfig = z.infer<typeof notificationsConfigSchema>;
export type NewsletterConfig = z.infer<typeof newsletterConfigSchema>;
export type TelegramChannelConfig = z.infer<typeof telegramChannelConfigSchema>;
export type ResendChannelConfig = z.infer<typeof resendChannelConfigSchema>;
export type SmtpChannelConfig = z.infer<typeof smtpChannelConfigSchema>;
export type BootstrapStatus = z.infer<typeof bootstrapStatusSchema>;
